import React, { useState, useEffect } from 'react';
import { HtmlTemplate, TemplateColumn, TemplateArea } from '../types/template';
import TemplateAreaManager from './TemplateAreaManager';
import './HtmlTemplateForm.css';

type TemplateTypeOption = {
  id: number;
  code: string;
  name: string;
};

interface HtmlTemplateFormProps {
  template: HtmlTemplate | null;
  templateTypes: TemplateTypeOption[];
  onSave: (template: HtmlTemplate & { columns?: TemplateColumn[]; areas?: TemplateArea[] }) => void;
  onCancel: () => void;
}

const HtmlTemplateForm: React.FC<HtmlTemplateFormProps> = ({ 
  template, 
  templateTypes, 
  onSave, 
  onCancel 
}) => {
  const [formData, setFormData] = useState<HtmlTemplate>({
    name: '',
    description: '',
    layout_type: '1-column',
    column_count: 1,
    is_responsive: true,
    responsive_breakpoints: {
      mobile: 768,
      tablet: 1024,
      desktop: 1200
    },
    is_active: true,
    is_published: true
  });

  const [columns, setColumns] = useState<TemplateColumn[]>([]);
  const [areas, setAreas] = useState<TemplateArea[]>([]);
  const [showAreaManager, setShowAreaManager] = useState(false);
  const [currentStep, setCurrentStep] = useState<'basic' | 'areas'>('basic');

  useEffect(() => {
    if (template) {
      // Автоматически вычисляем количество колонок на основе типа макета
      let columnCount: number;
      switch (template.layout_type) {
        case '1-column':
          columnCount = 1;
          break;
        case '2-column':
          columnCount = 2;
          break;
        case '3-column':
          columnCount = 3;
          break;
        case '2+sidebar':
          columnCount = 3; // 2 основные + 1 сайдбар
          break;
        case '3+sidebar':
          columnCount = 4; // 3 основные + 1 сайдбар
          break;
        default:
          columnCount = template.column_count || 1;
      }
      
      const updatedTemplate = { 
        ...template, 
        column_count: columnCount,
        is_published: template.is_published !== false
      };
      setFormData(updatedTemplate);
      // Создаем колонки по умолчанию на основе вычисленного column_count
      generateDefaultColumns(columnCount);
    } else {
      // Создаем колонки по умолчанию для нового шаблона
      generateDefaultColumns(1);
    }
  }, [template]);

  const generateDefaultColumns = (count: number) => {
    const defaultColumns: TemplateColumn[] = [];
    const columnNames = ['left', 'center', 'right', 'sidebar'];
    const widthValues = count === 1 ? ['100%'] : 
                      count === 2 ? ['30%', '70%'] : 
                      count === 3 ? ['25%', '50%', '25%'] : 
                      ['20%', '40%', '30%', '10%'];
    
    for (let i = 0; i < count; i++) {
      defaultColumns.push({
        column_name: columnNames[i] || `column-${i + 1}`,
        column_order: i + 1,
        width_type: 'percentage',
        width_value: widthValues[i] || 'auto',
        min_width: '200px',
        max_width: '100%',
        padding: { top: '16px', right: '16px', bottom: '16px', left: '16px' },
        margin: { top: '0', right: '0', bottom: '0', left: '0' },
        background_color: '#ffffff',
        border_style: 'solid',
        border_width: '1px',
        border_color: '#e9ecef',
        is_required: true
      });
    }
    
    setColumns(defaultColumns);
  };

  const handleLayoutTypeChange = (layoutType: string) => {
    let columnCount: number;
    switch (layoutType) {
      case '1-column':
        columnCount = 1;
        break;
      case '2-column':
        columnCount = 2;
        break;
      case '3-column':
        columnCount = 3;
        break;
      case '2+sidebar':
        columnCount = 3; // 2 основные + 1 сайдбар
        break;
      case '3+sidebar':
        columnCount = 4; // 3 основные + 1 сайдбар
        break;
      default:
        columnCount = 1;
    }
    
    setFormData(prev => ({ ...prev, layout_type: layoutType, column_count: columnCount }));
    generateDefaultColumns(columnCount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentStep === 'basic') {
      // Переходим к настройке областей
      setCurrentStep('areas');
      setShowAreaManager(true);
    } else {
      // Сохраняем шаблон с областями
      onSave({
        ...formData,
        columns,
        areas
      });
    }
  };

  const handleAreasChange = (newAreas: TemplateArea[]) => {
    setAreas(newAreas);
  };

  const handleFinalSave = () => {
    onSave({
      ...formData,
      columns,
      areas
    });
  };

  const handleBackToBasic = () => {
    setCurrentStep('basic');
    setShowAreaManager(false);
  };

  if (showAreaManager) {
    return (
      <div className="html-template-form">
        <div className="html-template-form__header">
          <h3>Настройка областей шаблона</h3>
          <p className="html-template-form__description">
            <strong>Шаг 2 из 2:</strong> Создайте области для типов компонентов. 
            При генерации сайта система автоматически выберет конкретные компоненты для каждой области.
          </p>
        </div>

        <TemplateAreaManager
          templateId={template?.id || 0}
          onAreasChange={handleAreasChange}
          onCancel={handleBackToBasic}
        />

        <div className="html-template-form__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleBackToBasic}
          >
            ← Назад к основным настройкам
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleFinalSave}
            disabled={areas.length === 0}
          >
            ✅ Создать шаблон
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="html-template-form">
      <div className="html-template-form__header">
        <h3>{template ? 'Редактирование HTML-шаблона' : 'Создание HTML-шаблона'}</h3>
        <p className="html-template-form__description">
          <strong>Шаг 1 из 2:</strong> Настройте основные параметры шаблона. 
          После этого вы сможете создать области для типов компонентов.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="html-template-form__form">
        <div className="html-template-form__section">
          <h4>Основные параметры</h4>
          
          <div className="form-group">
            <label htmlFor="name">Название шаблона *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Например: 2-колонка, 3-колонка с сайдбаром"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Описание</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Описание назначения и особенностей шаблона"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="layout_type">Тип макета *</label>
            <select
              id="layout_type"
              value={formData.layout_type}
              onChange={(e) => handleLayoutTypeChange(e.target.value)}
              required
            >
              <option value="1-column">1 колонка</option>
              <option value="2-column">2 колонки</option>
              <option value="3-column">3 колонки</option>
              <option value="2+sidebar">2 колонки + сайдбар</option>
              <option value="3+sidebar">3 колонки + сайдбар</option>
            </select>
            <small className="form-hint">
              Количество колонок автоматически установится в зависимости от выбранного типа макета
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="column_count">Количество колонок</label>
            <input
              type="number"
              id="column_count"
              value={formData.column_count}
              readOnly
              className="form-control--readonly"
            />
            <small className="form-hint">
              Это поле автоматически заполняется на основе выбранного типа макета
            </small>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_responsive}
                onChange={(e) => setFormData(prev => ({ ...prev, is_responsive: e.target.checked }))}
              />
              Адаптивный дизайн
            </label>
            <small className="form-hint">
              Шаблон будет автоматически адаптироваться под мобильные устройства
            </small>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              Активен
            </label>
            <small className="form-hint">
              Только активные шаблоны доступны для использования
            </small>
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_published !== false}
                onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
              />
              Опубликован
            </label>
            <small className="form-hint">
              Неопубликованные шаблоны доступны только администраторам
            </small>
          </div>
        </div>

        <div className="html-template-form__section">
          <h4>Предварительный просмотр колонок</h4>
          <div className="html-template-form__columns-preview">
            {columns.map((column, index) => (
              <div key={index} className="column-preview">
                <div className="column-preview__header">
                  <span className="column-preview__name">{column.column_name}</span>
                  <span className="column-preview__width">{column.width_value}</span>
                </div>
                <div className="column-preview__content">
                  Колонка {index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="html-template-form__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            Отмена
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!formData.name || formData.column_count === 0}
          >
            Далее: Настройка областей →
          </button>
        </div>
      </form>
    </div>
  );
};

export default HtmlTemplateForm;
